import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { formatGoalValue } from '../components/presentational/GoalsCard';
import USER_FILTER_TYPE, { HISTORY_RANGE } from '../constants';
import {
  getMaxEntryFieldConfig, resolveWeightUnitAbbr, getGoalSourceLabel, getMaxSourceLabel,
} from '../utils/maxesGoalsHelpers';
import {
  fetchMaxHistory, fetchHistoryCurrentMax, fetchAccountGoals, fetchGroups, fetchCalendars,
  fetchAthletes, fetchExercises, saveGoal, deleteGoals, setMaxActive,
  archiveMax, createMaxEfforts, setMaxesLock, resetMaxes, bulkArchiveMaxes,
  fetchMyGoals, createMyMaxEffort,
} from '../ducks/maxesGoalsActions';

/* * Helpers * */

export const withUnit = (value, unitAbbr) => {
  if (value === null || value === undefined || value === '') return null;
  const isFormattedTime = typeof value === 'string' && value.includes(':');
  return unitAbbr && !isFormattedTime ? `${value} ${unitAbbr}` : String(value);
};

const ACHIEVEMENT_SCOPE_TYPE = {
  1: 'Account', 2: 'Group', 3: 'Calendar', 4: 'Athlete',
};

const getWorkoutHref = (source, userId) => {
  if (source?.type === 'WORKOUT' && source.assignedWorkoutDate) {
    return `/workouts?date=${source.assignedWorkoutDate}&athleteId=${userId}`;
  }
  if (source?.type === 'DAY_BASED_WORKOUT' && source.dayBasedProgramId && source.dayNum) {
    return `/workouts?athleteId=${userId}&programId=${source.dayBasedProgramId}&dayNum=${source.dayNum}`;
  }
  return null;
};

const LAST_N = 10;

const getRangeQuery = (range) => {
  if (range === HISTORY_RANGE.Last10) return { limit: LAST_N };
  if (range === HISTORY_RANGE.Ytd) return { dateRangeStart: `${new Date().getFullYear()}-01-01` };
  return {};
};

const formatSignedSeconds = (seconds) => {
  const abs = Math.abs(seconds);
  return `${Math.floor(abs / 60)}:${String(Math.round(abs % 60)).padStart(2, '0')}`;
};

const useMaxHistory = (userId, exerciseId) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const { accountCode } = currentUser;
  const orgWeight = useSelector((state) => state.auth.data.currentUser?.userSettings?.orgWeight);
  const maxHistory = useSelector((state) => state.maxesGoals.data.maxHistory);
  const currentMax = useSelector((state) => state.maxesGoals.data.historyCurrentMax);
  const accountGoals = useSelector((state) => state.maxesGoals.data.accountGoals);
  const groups = useSelector((state) => state.maxesGoals.data.groups);
  const calendars = useSelector((state) => state.maxesGoals.data.calendars);
  const athletes = useSelector((state) => state.maxesGoals.data.athletes);
  const exercises = useSelector((state) => state.maxesGoals.data.exercises);
  const isMaxHistoryLoading = useSelector((state) => state.maxesGoals.ui.isMaxHistoryLoading);
  const isSavingGoal = useSelector((state) => state.maxesGoals.ui.isSavingGoal);
  const isDeletingGoals = useSelector((state) => state.maxesGoals.ui.isDeletingGoals);
  const isArchivingMax = useSelector((state) => state.maxesGoals.ui.isArchivingMax);
  const isCreatingMaxEffort = useSelector((state) => state.maxesGoals.ui.isCreatingMaxEffort);
  const isUpdatingMaxLock = useSelector((state) => state.maxesGoals.ui.isUpdatingMaxLock);
  const isResettingMaxes = useSelector((state) => state.maxesGoals.ui.isResettingMaxes);
  const isBulkArchivingMaxes = useSelector((state) => state.maxesGoals.ui.isBulkArchivingMaxes);

  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isAddRecordOpen, setIsAddRecordOpen] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [range, setRange] = useState(HISTORY_RANGE.Last10);

  // Athletes see their own history but can't edit goals, lock, clear or delete all
  const isAthleteView = !currentUser.admin;

  /* * Lookups * */

  const athleteById = useMemo(
    () => (isAthleteView ? [currentUser] : athletes).reduce((acc, athlete) => {
      acc[athlete.id] = athlete;
      return acc;
    }, {}),
    [isAthleteView, currentUser, athletes],
  );

  const exercisesById = useMemo(
    () => exercises.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {}),
    [exercises],
  );

  const athlete = athleteById[userId];
  const exercise = exercises.find((item) => item.id === exerciseId) || null;
  const exerciseName = exercise?.name || currentMax.exerciseName;
  const athleteName = athlete ? `${athlete.first} ${athlete.last}` : '';
  const fieldMode = getMaxEntryFieldConfig(exercise).mode;
  const unitAbbr = resolveWeightUnitAbbr(currentMax.unitAbbr, orgWeight);
  const isLowerBetter = Boolean(exercise?.isLowerNumberBetterForMaxes);

  /* * Goal * */

  const goalsWithSource = useMemo(
    () => accountGoals.map((goal) => ({
      ...goal,
      unitAbbr: resolveWeightUnitAbbr(goal.unitAbbr, orgWeight),
      sourceLabel: goal.isInherited
        ? getGoalSourceLabel(goal, { groups, calendars, athleteById })
        : null,
      inheritedGoalsInPriorityOrder: (goal.inheritedGoalsInPriorityOrder || [])
        .map((inheritedGoal) => ({
          ...inheritedGoal,
          unitAbbr: resolveWeightUnitAbbr(inheritedGoal.unitAbbr, orgWeight),
          sourceLabel: getGoalSourceLabel(inheritedGoal, { groups, calendars, athleteById }),
        })),
    })),
    [accountGoals, orgWeight, groups, calendars, athleteById],
  );

  const goal = goalsWithSource.find((item) => item.exerciseId === exerciseId) || null;

  // Editing a goal makes a new one so only achievements after it count
  const isAchievementOfCurrentGoal = useCallback((achievement) => {
    if (!goal || !goal.dateSet || !achievement) return false;
    const achievedOn = format(new Date(achievement.achievedAt * 1000), 'yyyy-MM-dd');
    return goal.appliesToType === ACHIEVEMENT_SCOPE_TYPE[achievement.applyToType]
      && (goal.appliesToId || 0) === achievement.applyToId
      && achievedOn >= goal.dateSet;
  }, [goal]);

  /* * History table * */

  // Also refreshes the goal since its progress depends on the current max
  const refreshMaxes = useCallback(() => {
    batch(() => {
      dispatch(fetchMaxHistory(accountCode, userId, exerciseId, {
        ...getRangeQuery(range), isMe: isAthleteView,
      }));
      dispatch(fetchHistoryCurrentMax(accountCode, userId, exerciseId, { isMe: isAthleteView }));
    });
  }, [accountCode, userId, exerciseId, range, isAthleteView, dispatch]);

  const handleSetActive = useCallback((row) => {
    dispatch(setMaxActive(accountCode, userId, exerciseId, row.id, {
      onSuccess: refreshMaxes, isMe: isAthleteView,
    }));
  }, [accountCode, userId, exerciseId, refreshMaxes, isAthleteView, dispatch]);

  const requestArchive = useCallback((row) => setPendingConfirm({ type: 'archiveOne', row }), []);

  const historyRows = useMemo(() => maxHistory.map((row) => {
    let displayChange = null;
    let isImprovement = null;
    if (row.change !== null && row.change !== undefined && row.change !== 0) {
      const sign = row.change > 0 ? '+' : '-';
      displayChange = fieldMode === 'timed'
        ? `${sign}${formatSignedSeconds(row.change)}`
        : `${sign}${withUnit(Math.abs(row.change), unitAbbr)}`;
      isImprovement = isLowerBetter ? row.change < 0 : row.change > 0;
    }
    let achievementValue = null;
    let achievementScope = null;
    if (row.achievement) {
      const scopeType = ACHIEVEMENT_SCOPE_TYPE[row.achievement.applyToType];
      const scopeName = scopeType === 'Athlete' ? null : getGoalSourceLabel(
        { appliesToType: scopeType, appliesToId: row.achievement.applyToId },
        { groups, calendars, athleteById },
      );
      achievementScope = scopeName || 'Individual';
      achievementValue = isAchievementOfCurrentGoal(row.achievement) ? formatGoalValue(goal) : null;
    }
    return {
      ...row,
      displayValue: withUnit(row.value, unitAbbr),
      displayChange,
      isImprovement,
      achievementValue,
      achievementScope,
      sourceLabel: getMaxSourceLabel(row.source, exercisesById),
      workoutHref: getWorkoutHref(row.source, userId),
      onSetActive: handleSetActive,
      onArchive: requestArchive,
    };
  }), [
    maxHistory, fieldMode, unitAbbr, isLowerBetter, groups, calendars, athleteById, goal,
    isAchievementOfCurrentGoal, userId, handleSetActive, requestArchive, exercisesById,
  ]);

  /* * Goal Actions * */

  const loadGoals = useCallback(() => {
    if (isAthleteView) {
      dispatch(fetchMyGoals(accountCode));
    } else {
      dispatch(fetchAccountGoals(accountCode, {
        filterType: USER_FILTER_TYPE.Athletes, filterIds: [userId], exerciseId,
      }));
    }
  }, [isAthleteView, accountCode, userId, exerciseId, dispatch]);

  const refreshAfterGoalChange = useCallback(() => {
    setIsGoalModalOpen(false);
    batch(() => {
      dispatch(fetchHistoryCurrentMax(accountCode, userId, exerciseId));
      loadGoals();
    });
  }, [accountCode, userId, exerciseId, loadGoals, dispatch]);

  const handleSubmitGoal = useCallback(({
    exerciseId: goalExerciseId, value, linkedExerciseId, percentage,
  }) => {
    dispatch(saveGoal(accountCode, {
      exerciseId: goalExerciseId,
      appliesToType: 'Athlete',
      appliesToId: userId,
      ...(linkedExerciseId ? { linkedExerciseId, percentage } : { value }),
    }, { onSuccess: refreshAfterGoalChange }));
  }, [accountCode, userId, refreshAfterGoalChange, dispatch]);

  const handleDeleteGoal = useCallback((goalId) => {
    dispatch(deleteGoals(accountCode, [goalId], { onSuccess: refreshAfterGoalChange }));
  }, [accountCode, refreshAfterGoalChange, dispatch]);

  const openGoalModal = () => setIsGoalModalOpen(true);
  const closeGoalModal = () => setIsGoalModalOpen(false);
  const openAddRecord = () => setIsAddRecordOpen(true);
  const closeAddRecord = () => setIsAddRecordOpen(false);
  const requestArchiveAll = () => setPendingConfirm({ type: 'archiveAll' });
  const closeConfirm = () => setPendingConfirm(null);

  const achievedRow = historyRows.find((row) => isAchievementOfCurrentGoal(row.achievement));
  const goalDisplay = goal?.linkedExercise
    ? formatGoalValue(goal)
    : withUnit(currentMax.goalValue, unitAbbr);

  /* * Current Max Card * */

  const hasCurrentMax = Boolean(currentMax.value);
  const isCurrentMaxLocked = Boolean(currentMax.isLocked);
  const athleteFirst = athlete?.first || 'this athlete';

  const handleClearCurrentMax = () => {
    dispatch(resetMaxes(accountCode, {
      filterType: USER_FILTER_TYPE.Athletes,
      filterId: userId,
      exerciseType: exercise?.type || currentMax.exerciseType,
      exerciseId,
    }, { onSuccess: refreshMaxes }));
  };

  const currentRow = historyRows.find((row) => row.isCurrentMax);
  const currentMaxDate = currentMax.dateSet || currentRow?.dateSet;

  const handleToggleLock = () => {
    dispatch(setMaxesLock(accountCode, {
      filterType: USER_FILTER_TYPE.Athletes,
      filterIds: [userId],
      exerciseIds: [exerciseId],
    }, !isCurrentMaxLocked, { onSuccess: refreshMaxes }));
  };

  /* * Add Max * */

  const handleSubmitAddRecord = ({
    dateSet, setCurrentMax, isLocked, entries,
  }) => {
    const fieldConfig = getMaxEntryFieldConfig(exercise);
    const items = entries.map((entry) => {
      const item = {
        userId: entry.userId,
        dateSet: format(dateSet, 'yyyy-MM-dd'),
        setCurrentMax: fieldConfig.mode === 'health' ? true : setCurrentMax,
        lockMax: isLocked,
      };
      if (fieldConfig.mode !== 'repsOnly') item.primary1 = Number(entry.primary1 || 0);
      if (fieldConfig.mode === 'timed' || fieldConfig.mode === 'feetInches') {
        item.primary2 = Number(entry.primary2 || 0);
      }
      if (fieldConfig.mode === 'weightReps' || fieldConfig.mode === 'repsOnly') {
        item.reps = Number(entry.reps || 0);
      }
      return item;
    });
    const handleCreated = () => {
      setIsAddRecordOpen(false);
      refreshMaxes();
    };
    if (isAthleteView) {
      const { userId: itemUserId, lockMax, ...myItem } = items[0];
      dispatch(createMyMaxEffort(accountCode, exerciseId, myItem, { onSuccess: handleCreated }));
      return;
    }
    dispatch(createMaxEfforts(accountCode, exerciseId, items, { onSuccess: handleCreated }));
  };

  /* * Confirm Popups * */

  const finishConfirm = () => {
    setPendingConfirm(null);
    refreshMaxes();
  };

  const handleConfirm = () => {
    if (pendingConfirm?.type === 'archiveOne') {
      dispatch(archiveMax(accountCode, userId, exerciseId, pendingConfirm.row.id, {
        onSuccess: finishConfirm, isMe: isAthleteView,
      }));
    } else if (pendingConfirm?.type === 'archiveAll') {
      dispatch(bulkArchiveMaxes(accountCode, {
        filterType: USER_FILTER_TYPE.Athletes,
        filterIds: [userId],
        exerciseType: exercise?.type || currentMax.exerciseType,
        exerciseIds: [exerciseId],
      }, { onSuccess: finishConfirm }));
    }
  };

  const confirmCopy = {
    archiveOne: {
      title: 'Delete Max',
      message: `Delete ${pendingConfirm?.row?.displayValue || 'this max'}? It will be removed from this history.${
        pendingConfirm?.row?.isCurrentMax ? ' The next best max will become the current max.' : ''}`,
      confirmLabel: 'Delete',
      isBusy: isArchivingMax,
    },
    archiveAll: {
      title: 'Delete all maxes?',
      message: `Every ${exerciseName} max for ${athleteName} is deleted, with the goals achieved by them.`,
      confirmLabel: 'Delete all',
      isBusy: isBulkArchivingMaxes,
    },
  }[pendingConfirm?.type] || {};

  /* * Data Loading * */

  useEffect(() => {
    if (!accountCode || !userId || !exerciseId) return;
    batch(() => {
      dispatch(fetchHistoryCurrentMax(accountCode, userId, exerciseId, { isMe: isAthleteView }));
      loadGoals();
    });
  }, [accountCode, userId, exerciseId, isAthleteView, loadGoals, dispatch]);

  useEffect(() => {
    if (!accountCode || !userId || !exerciseId) return;
    dispatch(fetchMaxHistory(accountCode, userId, exerciseId, {
      ...getRangeQuery(range), isMe: isAthleteView,
    }));
  }, [accountCode, userId, exerciseId, range, isAthleteView, dispatch]);

  useEffect(() => {
    if (!accountCode) return;
    batch(() => {
      if (!exercises.length) dispatch(fetchExercises(accountCode));
      if (isAthleteView) return;
      if (!athletes.length) dispatch(fetchAthletes(accountCode));
      if (!groups.length) dispatch(fetchGroups(accountCode));
      if (!calendars.length) dispatch(fetchCalendars(accountCode));
    });
  }, [accountCode]);

  /* * Props * */

  return {
    title: [athleteName, exerciseName && `${exerciseName} History`].filter(Boolean).join(' > '),
    currentMaxProps: {
      value: hasCurrentMax ? withUnit(currentMax.value, unitAbbr) : null,
      dateSet: currentMaxDate,
      sourceLabel: currentRow?.sourceLabel || null,
      athleteFirst,
      isLocked: isCurrentMaxLocked,
      isUpdatingLock: isUpdatingMaxLock,
      isClearing: isResettingMaxes,
      onClear: isAthleteView ? null : handleClearCurrentMax,
      onToggleLock: isAthleteView ? null : handleToggleLock,
    },
    historyHeaderProps: {
      onAddMax: openAddRecord,
      onDeleteAll: isAthleteView ? null : requestArchiveAll,
      // Older maxes may be hidden by the range so keep it enabled
      canDeleteAll: historyRows.length > 0 || range !== HISTORY_RANGE.All,
    },
    goalBlockProps: {
      goalValue: withUnit(currentMax.goalValue, unitAbbr),
      goalDetail: goal?.linkedExercise ? formatGoalValue(goal) : null,
      sourceLabel: goal?.sourceLabel,
      goalPct: currentMax.goalPct ?? null,
      goalColor: currentMax.goalColor,
      achievedGoal: achievedRow ? goalDisplay : null,
      achievedOn: achievedRow
        ? format(new Date(achievedRow.achievement.achievedAt * 1000), 'd MMM yyyy')
        : null,
      achievedWith: achievedRow?.displayValue,
      onSetGoal: isAthleteView ? null : openGoalModal,
    },
    chartProps: historyRows.length > 0 || range !== HISTORY_RANGE.All ? {
      points: historyRows,
      range,
      onRangeChange: setRange,
      isLoading: isMaxHistoryLoading,
      goalRawValue: currentMax.goalRawValue ?? null,
      isTimed: fieldMode === 'timed',
      unitLabel: fieldMode === 'timed' ? 'min:sec' : unitAbbr,
    } : null,
    tableProps: { rows: historyRows, isLoading: isMaxHistoryLoading },
    confirmProps: {
      isOpen: Boolean(pendingConfirm),
      title: confirmCopy.title,
      message: confirmCopy.message,
      confirmLabel: confirmCopy.confirmLabel,
      isBusy: confirmCopy.isBusy,
      onConfirm: handleConfirm,
      onClose: closeConfirm,
    },
    addRecordProps: {
      isOpen: isAddRecordOpen,
      title: `Add max for ${athleteName}: ${exerciseName || ''}`,
      athletes: [{ userId, first: athlete?.first, last: athlete?.last }],
      exercise,
      currentMaxByUserId: { [userId]: { value: currentMax.value, unitAbbr } },
      isSubmitting: isCreatingMaxEffort,
      allowLock: !isAthleteView,
      onSubmit: handleSubmitAddRecord,
      onClose: closeAddRecord,
    },
    setGoalProps: {
      isOpen: isGoalModalOpen,
      title: `Set a Goal for: ${athleteName}`,
      goalLabel: athlete ? `${athlete.first}'s individual goal` : 'Individual goal',
      exercises,
      goals: goalsWithSource,
      initialExerciseId: exerciseId,
      weightUnitAbbr: resolveWeightUnitAbbr('wgt', orgWeight),
      isSaving: isSavingGoal,
      isDeleting: isDeletingGoals,
      onSubmit: handleSubmitGoal,
      onDelete: handleDeleteGoal,
      onClose: closeGoalModal,
    },
  };
};

export default useMaxHistory;
